import { createFileRoute, Link } from "@tanstack/react-router";

import {
  ExternalAuthorityLink,
  LegalCallout,
  LegalDocument,
  LegalSection,
} from "@/features/legal/LegalDocument";
import { LEGAL_RESPONSIBLE } from "@/features/legal/legal-config";

export const Route = createFileRoute("/legal/privacidad")({
  head: () => ({
    meta: [
      { title: "Política de privacidad y tratamiento de datos | Socrattica" },
      {
        name: "description",
        content:
          "Política de privacidad y tratamiento de datos personales de Socrattica conforme al régimen colombiano.",
      },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalDocument
      eyebrow="Privacidad y tratamiento de datos"
      title="Tu información se usa con una finalidad concreta."
      summary="Esta política explica quién responde por tus datos, qué información tratamos al usar la tienda, la cuenta, la comunidad, el soporte y el boletín, y cómo puedes ejercer tus derechos."
    >
      <LegalCallout title="Versión corta">
        <p>
          Socrattica no vende tus datos. Usamos la información necesaria para operar tu cuenta,
          gestionar compras y descargas, responder soporte y —solo si lo autorizas— enviarte el
          boletín o medir el uso del sitio con Firebase Analytics.
        </p>
      </LegalCallout>

      <LegalSection id="responsable" title="1. Responsable del tratamiento">
        <p>
          El responsable es <strong>{LEGAL_RESPONSIBLE.name}</strong>, {LEGAL_RESPONSIBLE.capacity},
          con domicilio y operación en {LEGAL_RESPONSIBLE.country}. Puedes presentar consultas,
          reclamos, solicitudes de actualización, corrección, supresión o revocatoria en{" "}
          <a href={`mailto:${LEGAL_RESPONSIBLE.email}`}>{LEGAL_RESPONSIBLE.email}</a> o mediante el{" "}
          <Link to="/productos/tickets">centro privado de soporte</Link> si tienes una cuenta.
        </p>
        <p>
          Nuestro objetivo de primera respuesta es de {LEGAL_RESPONSIBLE.responseTime}, sin
          perjuicio de los plazos legales detallados en la sección 11.
        </p>
      </LegalSection>

      <LegalSection id="alcance" title="2. Alcance y marco aplicable">
        <p>
          Esta política aplica al sitio web de Socrattica, su librería de productos digitales, la
          cuenta de usuario, compras, descargas, soporte, pódcast, boletín y panel administrativo.
          Está orientada por la Ley 1581 de 2012 y el Decreto 1074 de 2015 de Colombia, además de
          las normas que los modifiquen o complementen.
        </p>
        <p>
          Puedes consultar la{" "}
          <ExternalAuthorityLink href="https://www1.funcionpublica.gov.co/eva/gestornormativo/norma.php?i=49981">
            Ley 1581 de 2012
          </ExternalAuthorityLink>{" "}
          y la información de la{" "}
          <ExternalAuthorityLink href="https://sedeelectronica.sic.gov.co/temas/proteccion-de-datos-personales">
            Superintendencia de Industria y Comercio
          </ExternalAuthorityLink>
          .
        </p>
      </LegalSection>

      <LegalSection id="datos" title="3. Datos que podemos tratar">
        <ul>
          <li>
            <strong>Cuenta e identidad:</strong> nombre, correo, identificador de Firebase,
            proveedor de acceso, estado de verificación y rol. Si eliges Google, recibimos los datos
            básicos que autorices en ese proveedor.
          </li>
          <li>
            <strong>Compras:</strong> correo del comprador, productos, cantidades, valor, moneda,
            fecha, estado, referencia de pedido, identificadores de transacción, pasarela usada y
            datos del comprobante. Socrattica no almacena el número completo ni el código de
            seguridad de tu tarjeta.
          </li>
          <li>
            <strong>Descargas:</strong> pedido y producto cuya autorización se verifica, junto con
            la fecha de aprobación y los datos técnicos necesarios para entregar el archivo sin
            publicar su ubicación permanente.
          </li>
          <li>
            <strong>Soporte:</strong> nombre, correo, categoría, prioridad, asunto, mensajes,
            referencias, fechas y estado del caso.
          </li>
          <li>
            <strong>Comunidad del pódcast:</strong> episodio, reflexión que decides publicar, primer
            nombre y foto de perfil de Google visibles cuando estén disponibles, reacciones,
            reportes, fechas y estado de moderación. Las notas del Laboratorio permanecen en tu
            navegador y solo se transmiten si marcas “Compartir públicamente” y pulsas “Publicar”.
          </li>
          <li>
            <strong>Boletín:</strong> correo, fecha, versión y origen del consentimiento, estado de
            doble confirmación, etiquetas y actividad básica gestionada por Mailchimp.
          </li>
          <li>
            <strong>Datos técnicos:</strong> dirección IP y metadatos de solicitud en registros de
            seguridad y limitación de abuso. Firebase App Check y reCAPTCHA Enterprise pueden
            generar señales de integridad y riesgo para comprobar que la solicitud procede del sitio
            legítimo. Si autorizas analítica, también eventos de navegación, dispositivo o
            navegador, ubicación aproximada y un identificador de Analytics.
          </li>
          <li>
            <strong>Preferencias locales:</strong> carrito, moneda, catálogo en caché, estado del
            aviso del boletín y decisión de cookies guardados en tu navegador.
          </li>
        </ul>
        <p>
          No solicitamos datos sensibles ni información de pago completa. Evita incluir datos
          sensibles o de terceros en mensajes de soporte.
        </p>
      </LegalSection>

      <LegalSection id="finalidades" title="4. Finalidades del tratamiento">
        <ul>
          <li>Crear, autenticar y proteger tu cuenta; recuperar el acceso y verificar roles.</li>
          <li>Preparar, confirmar y registrar pedidos, pagos, comprobantes y devoluciones.</li>
          <li>
            Vincular compras anteriores con un correo verificado y habilitar descargas seguras.
          </li>
          <li>Atender, identificar y dar trazabilidad a solicitudes, quejas y reclamos.</li>
          <li>
            Publicar las reflexiones que compartes voluntariamente, mostrar reacciones y aplicar
            reportes, límites de uso y moderación para cuidar la comunidad.
          </li>
          <li>Prevenir fraude, abuso, accesos no autorizados e incidentes de seguridad.</li>
          <li>Cumplir obligaciones contables, tributarias, contractuales y de consumo.</li>
          <li>
            Enviar nuevos episodios y novedades únicamente cuando otorgues consentimiento, y
            gestionar tu baja.
          </li>
          <li>
            Medir de forma agregada el rendimiento, navegación y uso de la tienda únicamente si
            autorizas cookies de analítica.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="bases" title="5. Autorización y otras bases de tratamiento">
        <p>
          Pedimos autorización previa, expresa e informada cuando corresponde, por ejemplo en el
          boletín y en Analytics. Los datos indispensables para crear una cuenta, atender una
          compra, entregar un archivo o responder una solicitud se tratan para ejecutar la relación
          solicitada por ti y cumplir obligaciones legales.
        </p>
        <p>
          Puedes retirar el consentimiento para finalidades opcionales sin afectar la licitud del
          tratamiento anterior. La revocatoria no impide conservar información cuando exista un
          deber legal o contractual vigente.
        </p>
      </LegalSection>

      <LegalSection id="terceros" title="6. Encargados, proveedores y transferencias">
        <p>Para prestar el servicio podemos compartir únicamente los datos necesarios con:</p>
        <ul>
          <li>
            <strong>Google Firebase:</strong> alojamiento, autenticación, base de datos, archivos,
            funciones y seguridad.
          </li>
          <li>
            <strong>Google Analytics for Firebase:</strong> medición opcional, solo después de tu
            autorización.
          </li>
          <li>
            <strong>Google Sign-In:</strong> autenticación opcional cuando eliges continuar con
            Google.
          </li>
          <li>
            <strong>Firebase App Check y reCAPTCHA Enterprise:</strong> validación necesaria y
            silenciosa de solicitudes para prevenir bots, fraude y abuso de los servicios.
          </li>
          <li>
            <strong>YouTube Data API:</strong> consulta de la playlist pública, títulos, fechas,
            duración y miniaturas del canal. El sitio no inserta el reproductor; al abrir un video
            pasas a YouTube bajo sus propias condiciones.
          </li>
          <li>
            <strong>Mailchimp:</strong> gestión del boletín, altas, bajas y etiquetas de audiencia.
          </li>
          <li>
            <strong>ePayco u otra pasarela indicada en el checkout:</strong> procesamiento del pago
            y confirmación de su resultado.
          </li>
          <li>Autoridades o asesores cuando una norma o una defensa jurídica lo exija.</li>
        </ul>
        <p>
          Algunos proveedores pueden procesar datos fuera de Colombia. En esos casos usamos
          servicios sujetos a condiciones de privacidad y seguridad y aplicamos las reglas
          colombianas sobre transmisión o transferencia internacional que correspondan.
        </p>
      </LegalSection>

      <LegalSection id="conservacion" title="7. Conservación">
        <ul>
          <li>Datos de cuenta: mientras la cuenta esté activa y después por el tiempo exigible.</li>
          <li>
            Pedidos, pagos y comprobantes: durante los plazos contables, tributarios, comerciales y
            de protección al consumidor aplicables, que pueden alcanzar diez años.
          </li>
          <li>
            Soporte: mientras el caso esté abierto y durante el periodo necesario para seguimiento,
            defensa y cumplimiento.
          </li>
          <li>
            Aportes comunitarios: mientras permanezcan publicados o hasta que los elimines; los
            registros mínimos de reporte o moderación pueden conservarse durante el periodo
            necesario para prevenir abuso y resolver reclamaciones.
          </li>
          <li>Boletín: hasta que canceles la suscripción o solicites la supresión procedente.</li>
          <li>
            Analytics: según la retención configurada en Google Analytics y mientras exista
            consentimiento; puedes revocarlo en cualquier momento.
          </li>
          <li>
            Datos locales: hasta que cambies la preferencia, borres los datos del navegador o venza
            la caché correspondiente.
          </li>
        </ul>
        <p>
          Una solicitud de eliminación no obliga a borrar registros que debamos conservar por ley,
          para una transacción pendiente o para formular, ejercer o defender reclamaciones.
        </p>
      </LegalSection>

      <LegalSection id="seguridad" title="8. Seguridad y confidencialidad">
        <p>
          Aplicamos acceso autenticado, reglas de Firebase, enlaces de descarga temporales,
          validación de roles, App Check con reCAPTCHA Enterprise, cifrado en tránsito, limitación
          de solicitudes y acceso administrativo restringido. Ningún sistema es infalible; si
          identificamos un incidente relevante actuaremos para contenerlo, documentarlo y notificar
          cuando corresponda.
        </p>
        <p>
          Tú también debes proteger tu contraseña, cerrar sesión en equipos compartidos y avisarnos
          si sospechas de un acceso no autorizado.
        </p>
      </LegalSection>

      <LegalSection id="menores" title="9. Niñas, niños y adolescentes">
        <p>
          El contenido educativo puede ser útil para distintas edades, pero las compras y la
          creación autónoma de cuentas están dirigidas a personas con capacidad para contratar. Si
          eres menor de edad, usa el sitio con acompañamiento y autorización de tu madre, padre o
          representante. No tratamos intencionalmente datos de menores para publicidad.
        </p>
      </LegalSection>

      <LegalSection id="derechos" title="10. Tus derechos">
        <ul>
          <li>Conocer, actualizar y rectificar tus datos.</li>
          <li>Solicitar prueba de la autorización cuando sea exigible.</li>
          <li>Conocer el uso que se ha dado a tus datos.</li>
          <li>Presentar quejas ante la SIC después de agotar el trámite con Socrattica.</li>
          <li>Revocar la autorización o solicitar supresión cuando proceda.</li>
          <li>Acceder gratuitamente a tus datos personales tratados.</li>
        </ul>
      </LegalSection>

      <LegalSection id="procedimiento" title="11. Consultas, reclamos y plazos">
        <p>
          Envía tu solicitud a{" "}
          <a href={`mailto:${LEGAL_RESPONSIBLE.email}`}>{LEGAL_RESPONSIBLE.email}</a> indicando
          nombre, correo asociado, descripción clara, derecho que deseas ejercer y los documentos
          que acrediten tu identidad o representación cuando hagan falta.
        </p>
        <ul>
          <li>
            <strong>Consultas:</strong> se atienden en máximo diez días hábiles desde su recepción.
            Si no es posible, informaremos la razón y la nueva fecha, que no excederá cinco días
            hábiles adicionales.
          </li>
          <li>
            <strong>Reclamos:</strong> se atienden en máximo quince días hábiles desde el día
            siguiente a su recepción completa. Si no es posible, informaremos la razón y la nueva
            fecha, que no excederá ocho días hábiles adicionales.
          </li>
          <li>
            Si el reclamo está incompleto, pediremos la corrección dentro de los cinco días
            siguientes a su recepción.
          </li>
        </ul>
        <p>
          Para dejar de recibir el boletín puedes usar el enlace de cancelación incluido en cada
          mensaje; para Analytics, abre “Preferencias de cookies” en el pie del sitio.
        </p>
      </LegalSection>

      <LegalSection id="cambios" title="12. Cambios a esta política">
        <p>
          Podemos actualizar esta política por cambios legales, técnicos o del servicio.
          Publicaremos la nueva fecha de vigencia y, si el cambio altera de forma sustancial una
          finalidad autorizada, solicitaremos una nueva autorización cuando sea necesario.
        </p>
      </LegalSection>
    </LegalDocument>
  );
}
